/**
 * usePageScopeProxy
 *
 * Owns a scope opener's PageScope proxy. The proxy is created once per
 * mount (stable identity, so the context value never churns and deep
 * consumers never re-render from provider identity) and changed fields
 * are synced during render behind an `Object.is` guard. The guarded
 * render-time write is idempotent (StrictMode-safe) and synchronous
 * within the pass — children rendering after the opener snapshot the new
 * ids in the same commit, which is what keeps a leaf's resolved
 * (entityId, recordId) equal to its opener's props at every commit.
 * A write reached from an abandoned concurrent render self-heals: it
 * bumps the proxy version and re-renders subscribed leaves with the
 * final values. `source` must never change for a mounted opener — remount
 * with a React key instead.
 */
import { useState } from 'react';
import { proxy } from 'valtio';
import type { PageScope } from './PageScopeContext';

export const usePageScopeProxy = <S extends PageScope>(value: S): S => {
    const [scope] = useState(() => proxy({ ...value }));
    const syncKey = <K extends keyof S>(key: K): void => {
        if (!Object.is(scope[key], value[key])) {
            scope[key] = value[key];
        }
    };
    (Object.keys(value) as (keyof S)[]).forEach(syncKey);
    return scope;
};
